import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";

const menuItems = [
  { icon: "ri-dashboard-line", label: "控制台", path: "/platform/dashboard" },
  { icon: "ri-user-settings-line", label: "代理商管理", path: "/platform/agents" },
  { icon: "ri-vip-crown-line", label: "套餐管理", path: "/platform/plans" },
  { icon: "ri-global-line", label: "站点总览", path: "/platform/sites" },
  { icon: "ri-settings-3-line", label: "系统设置", path: "/platform/settings" },
];

interface PlatformLayoutProps {
  children: React.ReactNode;
}

export default function PlatformLayout({ children }: PlatformLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const currentLabel =
    menuItems.find((m) => location.pathname === m.path || location.pathname.startsWith(m.path + "/"))?.label || "平台管理";

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-full bg-gray-900 text-white transition-all duration-300 z-40 flex flex-col ${
          sidebarCollapsed ? "w-16" : "w-56"
        }`}
      >
        {/* Logo */}
        <div className="flex items-center gap-3 px-4 py-5 border-b border-gray-700/50 shrink-0">
          <div className="w-8 h-8 flex items-center justify-center bg-blue-500 rounded-lg shrink-0">
            <i className="ri-admin-line text-white text-base" />
          </div>
          {!sidebarCollapsed && (
            <div>
              <div className="text-sm font-bold text-white">平台管理</div>
              <div className="text-xs text-gray-400">Platform Admin</div>
            </div>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 py-3 overflow-y-auto">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + "/");
            return (
              <Link
                key={item.path}
                to={item.path}
                title={sidebarCollapsed ? item.label : undefined}
                className={`flex items-center gap-3 px-4 py-2.5 mx-2 rounded-lg mb-1 transition-colors ${
                  isActive
                    ? "bg-blue-500 text-white"
                    : "text-gray-400 hover:bg-gray-800 hover:text-white"
                }`}
              >
                <div className="w-5 h-5 flex items-center justify-center shrink-0">
                  <i className={`${item.icon} text-base`} />
                </div>
                {!sidebarCollapsed && (
                  <span className="text-sm font-medium whitespace-nowrap">{item.label}</span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Bottom */}
        <div className="border-t border-gray-700/50 p-3 shrink-0 space-y-1">
          <Link
            to="/"
            title={sidebarCollapsed ? "返回首页" : undefined}
            className="flex items-center gap-3 px-2 py-2 w-full rounded-lg text-gray-400 hover:bg-gray-800 hover:text-white transition-colors"
          >
            <div className="w-5 h-5 flex items-center justify-center shrink-0">
              <i className="ri-home-4-line text-base" />
            </div>
            {!sidebarCollapsed && <span className="text-sm">返回首页</span>}
          </Link>
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="flex items-center gap-3 px-2 py-2 w-full rounded-lg text-gray-400 hover:bg-gray-800 hover:text-white transition-colors cursor-pointer"
          >
            <div className="w-5 h-5 flex items-center justify-center shrink-0">
              <i className={`${sidebarCollapsed ? "ri-arrow-right-s-line" : "ri-arrow-left-s-line"} text-base`} />
            </div>
            {!sidebarCollapsed && <span className="text-sm">收起菜单</span>}
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className={`flex-1 flex flex-col transition-all duration-300 ${sidebarCollapsed ? "ml-16" : "ml-56"}`}>
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 h-14 flex items-center justify-between px-6 sticky top-0 z-30">
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <Link to="/" className="hover:text-blue-500 transition-colors flex items-center gap-1">
              <i className="ri-home-4-line" />
              <span>首页</span>
            </Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/platform/dashboard" className="hover:text-blue-500 transition-colors">平台管理</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-gray-800 font-medium">{currentLabel}</span>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-blue-50 rounded-full">
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-admin-line text-blue-500 text-sm" />
              </div>
              <span className="text-sm font-medium text-blue-600">{user?.nickname}</span>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-logout-box-r-line text-sm" />
              </div>
              <span>退出</span>
            </button>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 p-6 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
